import React, { useEffect, useState } from 'react'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetIntegrationUsersQuery } from '../../../store/integrations/integrations'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../common/errorMessages'
import { Controller, useFormContext } from 'react-hook-form'
import { FormFeedback } from '../../../components/Form/FormFeedback'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../models/Account'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { useMeetingUsers } from '../../../apps/professional-app/fa/MeetingSummary/hooks/users'
import { selectRoomSession } from '../../../store/meetingSummary/roomSession'
import { getContactDropdownComponent } from '../ContactDropdownItems'
import { IntegrationServiceId } from '../../../common/integrations'
import { getContactOptions } from './utils'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

const CONTACT_MAPPING_LABEL_MAPPING: { [key: string]: string } = {
  contact: 'Contact',
  account: 'Account',
}

export interface ContactSelectDropdownProps {
  serviceId: string
  onContactSelected: (option: SelectOption) => void
  initialSelectedOptionExternalId?: string
  isLoading?: boolean
}

export const ContactSelectDropdown: React.FC<ContactSelectDropdownProps> = ({ serviceId, onContactSelected, initialSelectedOptionExternalId, isLoading }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError, isLoading: triggerGetContactsLoading }] =
    useLazyGetIntegrationUsersQuery()
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])

  const [initialOption, setInitialOption] = useState<SelectOption | null>(null)
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(true)

  const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
  const contactMapping = serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] ?? 'contact'
  const contactLabel =
    typeof contactMapping === 'string'
      ? CONTACT_MAPPING_LABEL_MAPPING[contactMapping]
      : (contactMapping.displayName ?? CONTACT_MAPPING_LABEL_MAPPING[contactMapping.objectName] ?? 'Contact')
  useEffect(() => {
    const fetchAndSetInitialOption = async () => {
      if (!initialSelectedOptionExternalId || !isLoadingInitial) {
        setIsLoadingInitial(false)
        return
      }

      try {
        const contactsData = await triggerGetContacts({
          accountId,
          connector: serviceId,
          filters: { externalIds: [initialSelectedOptionExternalId] },
        }).unwrap()

        if (contactsData?.data && contactsData.data.length > 0) {
          const initialSelectOptions = getContactOptions(contactsData.data, serviceId)
          setContactOptions(initialSelectOptions)

          setInitialOption(initialSelectOptions[0])

          onContactSelected(initialSelectOptions[0])
        }
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoadingInitial(false)
      }
    }

    fetchAndSetInitialOption()
  }, [serviceId, triggerGetContacts, onContactSelected, accountId, initialSelectedOptionExternalId, isLoadingInitial])
  const onSearchInputChange = async (searchInput: string) => {
    if (isLoadingInitial) {
      return
    }
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (isAccountFetching || isLoadingInitial) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage))) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  const handleOnOptionSelected = (option: SelectOption) => {
    onContactSelected(option)
  }

  return (
    <div>
      <div className='lead-extra-small'>{contactLabel ?? 'Contact'}</div>
      <SelectDropdown
        initialSelectedOption={initialOption}
        options={contactOptions}
        className='py-2 zocks-px-0.75'
        onOptionSelected={handleOnOptionSelected}
        DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
        searchable={true}
        onSearchInputChange={onSearchInputChange}
        placeholder={`Select your ${contactLabel}`}
        loading={triggerGetContactsLoading || isLoading}
      />
    </div>
  )
}

export interface ContactFormSelectDropdownProps {
  fieldName: string
  contactOptions: SelectOption[]
  serviceId: string
  onSearchInputChange: (searchInput: string) => Promise<void>
  onOptionSelected?: (option: SelectOption) => void
  showLabel?: boolean
  isLoading?: boolean
}

export const ContactFormSelectDropdown: React.FC<ContactFormSelectDropdownProps> = ({
  fieldName,
  contactOptions,
  serviceId,
  onSearchInputChange,
  onOptionSelected,
  showLabel = true,
  isLoading = false,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [triggerGetContacts, { isLoading: getContactsLoading }] = useLazyGetIntegrationUsersQuery()
  const [suggestedCRMContact, setSuggestedCRMContact] = useState<SelectOption | null>(null)
  const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
  const roomSession = useAppSelector(selectRoomSession)
  const { usersByRoles } = useMeetingUsers(roomSession)
  const [contactDropdownOptions, setContactDropdownOptions] = useState<SelectOption[]>(contactOptions)

  const contactMapping = serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] ?? 'contact'
  const contactLabel =
    typeof contactMapping === 'string'
      ? CONTACT_MAPPING_LABEL_MAPPING[contactMapping]
      : (contactMapping.displayName ?? CONTACT_MAPPING_LABEL_MAPPING[contactMapping.objectName] ?? 'Contact')
  const { control, setValue } = useFormContext()

  useEffect(() => {
    if (suggestedCRMContact || !usersByRoles) {
      return
    }
    const clients = usersByRoles['Clients']
    if (!clients || clients.length !== 1) {
      return
    }
    const contact = clients[0]
    const integration = contact.integrations?.find((i) => i.serviceId === serviceId)
    if (!integration) {
      return
    }

    triggerGetContacts({
      accountId,
      connector: serviceId,
      filters: { externalIds: [integration.externalId] },
    }).then((contactsData) => {
      if (contactsData && contactsData.data) {
        const suggestedOptions = getContactOptions(contactsData.data.data, serviceId)
        if (suggestedOptions.length) {
          setSuggestedCRMContact(suggestedOptions[0])
          setValue(fieldName, suggestedOptions[0].id)
          if (onOptionSelected) {
            onOptionSelected(suggestedOptions[0])
          }
        }
      }
    })
  }, [triggerGetContacts, accountId, serviceId, suggestedCRMContact, usersByRoles, onOptionSelected, setValue, fieldName])

  useEffect(() => {
    const newDropdownOptions = [...contactOptions]
    if (suggestedCRMContact && !newDropdownOptions.find((option) => option.id === suggestedCRMContact.id)) {
      newDropdownOptions.push(suggestedCRMContact)
    }
    setContactDropdownOptions(newDropdownOptions)
  }, [contactOptions, suggestedCRMContact])

  if (isAccountFetching) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }
  return (
    <>
      <div className='lead-extra-small'>{(showLabel && contactLabel) ?? 'Contact'}</div>
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: `${contactLabel} is required.` }}
        render={({ field, fieldState }) => {
          const handleOptionSelected = (option: SelectOption) => {
            field.onChange(option.id)
            if (onOptionSelected) {
              onOptionSelected(option)
            }
          }
          return (
            <>
              <SelectDropdown
                initialSelectedOption={suggestedCRMContact}
                options={contactDropdownOptions}
                className='py-2 zocks-px-0.75'
                onOptionSelected={handleOptionSelected}
                DropDownItemOptionComponent={getContactDropdownComponent(serviceId as IntegrationServiceId)}
                searchable={true}
                onSearchInputChange={onSearchInputChange}
                placeholder={`Select your ${contactLabel}`}
                loading={isLoading || getContactsLoading}
              />
              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
            </>
          )
        }}
      />
    </>
  )
}
